import { type ForwardedRef, type ReactNode, forwardRef } from "react";

import { cn } from "$/lib/utils/functions/misc.functions";
import type { ComboboxInputOptionListProps } from "./ComboboxInput.types";
import ComboboxInputOption from "./ComboboxInputOption";

function ComboboxInputOptionsListInner<T>(
  {
    id,
    options,
    focusedOptionIndex,
    multi,
    selected,
    handleSetFocusedOptionIndex,
    handleSelect,
  }: ComboboxInputOptionListProps<T>,
  listRef: ForwardedRef<HTMLUListElement>,
) {
  if (options.length === 0) {
    return (
      <div className="flex h-56 flex-col items-center justify-center gap-2 pb-6">
        <p className="text-grey-400 text-center text-sm">
          Aucun résultat trouvé
        </p>
      </div>
    );
  }

  return (
    <ul
      id={id}
      ref={listRef}
      role="listbox"
      aria-multiselectable={multi}
      aria-activedescendant={
        focusedOptionIndex !== -1 ? `option-${focusedOptionIndex}` : undefined
      }
      className="scrollbar-custom z-50 max-h-56 space-y-1 overflow-auto overscroll-contain bg-white pb-1"
    >
      {options.map((option, i) => {
        let isSelected = false;
        if (selected) {
          if (multi) {
            if (!Array.isArray(selected)) {
              throw new Error("Multi select options must be an array");
            }
            isSelected = selected.includes(option.value);
          } else {
            isSelected = selected === option.value;
          }
        }

        return (
          <li
            key={i}
            role="option"
            data-focused={focusedOptionIndex === i}
            aria-selected={isSelected}
            title={option.label}
            onMouseOver={() => handleSetFocusedOptionIndex(i)}
            tabIndex={-1}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSelect(option.value, i);
            }}
            className={cn(
              "rounded-md px-2 py-1 text-start font-medium text-black transition-colors *:cursor-pointer hover:bg-tertiary-200 data-[focused=true]:bg-tertiary-200",
              {
                "bg-tertiary-200": isSelected,
              },
            )}
          >
            {option.render?.({
              value: option.value,
              label: option.label,
              isSelected,
              multi,
            }) ?? (
              <ComboboxInputOption
                label={option.label}
                isSelected={isSelected}
                multi={multi}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

const ComboboxInputOptionsList = forwardRef(ComboboxInputOptionsListInner) as {
  <T>(
    props: ComboboxInputOptionListProps<T> & {
      ref?: ForwardedRef<HTMLUListElement>;
    },
  ): ReactNode;
  displayName: string;
};
ComboboxInputOptionsList.displayName = "ComboboxInputOptionsList";

export default ComboboxInputOptionsList;
